import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Plus,
  Search,
  Copy,
  Star,
  StarOff,
  Filter,
  Mail,
  MoreHorizontal,
  FileText,
  FolderOpen,
  Trash,
  Pencil,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { EmailTemplateEditor } from "@/components/communications/EmailTemplateEditor";
import { EmailTemplate } from "@/types/communication.types";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";
import { ActionMenu } from "@/components/ui/action-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  fetchEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  duplicateEmailTemplate,
  setDefaultTemplate,
  getTemplateCategories,
  getDefaultTemplateCategories,
} from "@/services/communication/emailTemplateService";

export default function EmailTemplates() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // States for UI controls
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  
  // Query for email templates
  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
  } = useQuery({
    queryKey: ["emailTemplates", selectedCategory],
    queryFn: () => fetchEmailTemplates({
      category: selectedCategory || undefined,
    }),
  });
  
  // Query for template categories
  const {
    data: categories = [],
    isLoading: isLoadingCategories,
  } = useQuery({
    queryKey: ["templateCategories"],
    queryFn: getTemplateCategories,
  });
  
  // Create mutation
  const createMutation = useMutation({
    mutationFn: createEmailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailTemplates"] });
      queryClient.invalidateQueries({ queryKey: ["templateCategories"] });
      toast({
        title: "Success",
        description: "Email template created successfully",
      });
      setEditorOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create email template",
        variant: "destructive",
      });
    },
  });
  
  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      updateEmailTemplate(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailTemplates"] });
      toast({
        title: "Success",
        description: "Email template updated successfully",
      });
      setEditorOpen(false);
      setSelectedTemplate(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update email template",
        variant: "destructive",
      });
    },
  });
  
  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteEmailTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailTemplates"] });
      toast({
        title: "Success",
        description: "Email template deleted successfully",
      });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete email template",
        variant: "destructive",
      });
    },
  });
  
  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: ({ id, newName }: { id: string; newName?: string }) =>
      duplicateEmailTemplate(id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailTemplates"] });
      toast({
        title: "Success",
        description: "Email template duplicated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to duplicate email template",
        variant: "destructive",
      });
    },
  });
  
  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: ({ id, category }: { id: string; category: string }) =>
      setDefaultTemplate(id, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emailTemplates"] });
      toast({
        title: "Success",
        description: "Default template updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to set default template",
        variant: "destructive",
      });
    },
  });
  
  // Filter templates by search term
  const filteredTemplates = templates.filter((template) => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      template.name.toLowerCase().includes(searchTermLower) ||
      template.subject.toLowerCase().includes(searchTermLower) ||
      template.content.toLowerCase().includes(searchTermLower)
    );
  });
  
  // Handle form submission
  const handleSaveTemplate = async (values: any) => {
    if (selectedTemplate) {
      await updateMutation.mutateAsync({
        id: selectedTemplate.id,
        updates: {
          ...values,
          updated_at: new Date().toISOString(),
        },
      });
    } else {
      await createMutation.mutateAsync({
        ...values,
        created_by: user?.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
  };
  
  // Handle edit click
  const handleEditClick = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setEditorOpen(true);
  };
  
  // Handle duplicate click
  const handleDuplicateClick = (id: string) => {
    duplicateMutation.mutate({ id });
  };
  
  // Handle delete click
  const handleDeleteClick = (id: string) => {
    setTemplateToDelete(id);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (templateToDelete) {
      await deleteMutation.mutateAsync(templateToDelete);
    }
  };
  
  // Handle set as default
  const handleSetAsDefault = (id: string, category: string) => {
    setDefaultMutation.mutate({ id, category });
  };
  
  // Group templates by category
  const groupedTemplates: Record<string, EmailTemplate[]> = {};
  filteredTemplates.forEach((template) => {
    if (!groupedTemplates[template.category]) {
      groupedTemplates[template.category] = [];
    }
    groupedTemplates[template.category].push(template);
  });
  
  // Handle add new template
  const handleAddNew = () => {
    setSelectedTemplate(null);
    setEditorOpen(true);
  };

  if (isLoadingTemplates) {
    return (
      <div className="flex h-[calc(100vh-120px)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">Email Templates</h1>
        <Button onClick={handleAddNew}>
          <Plus className="mr-2 h-4 w-4" />
          New Template
        </Button>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 w-full"
          />
        </div>
        
        <Select
          value={selectedCategory || "all"}
          onValueChange={(value) => setSelectedCategory(value === "all" ? null : value)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <Tabs defaultValue="grid" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="grid">Grid View</TabsTrigger>
          <TabsTrigger value="list">List View</TabsTrigger>
          <TabsTrigger value="category">Category View</TabsTrigger>
        </TabsList>
        
        <TabsContent value="grid">
          {filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">
                  No templates found. Create your first template by clicking the "New Template" button.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredTemplates.map((template) => (
                <Card key={template.id} className="relative">
                  <div className="absolute top-2 right-2">
                    <ActionMenu
                      actions={[
                        {
                          label: "Edit",
                          onClick: () => handleEditClick(template),
                          icon: <Pencil className="h-4 w-4" />,
                        },
                        {
                          label: "Duplicate",
                          onClick: () => handleDuplicateClick(template.id),
                          icon: <Copy className="h-4 w-4" />,
                        },
                        {
                          label: template.is_default
                            ? "Remove Default"
                            : "Set as Default",
                          onClick: () => handleSetAsDefault(
                            template.id,
                            template.category
                          ),
                          icon: template.is_default ? (
                            <StarOff className="h-4 w-4" />
                          ) : (
                            <Star className="h-4 w-4" />
                          ),
                        },
                        {
                          label: "Delete",
                          onClick: () => handleDeleteClick(template.id),
                          variant: "destructive",
                          icon: <Trash className="h-4 w-4" />,
                        },
                      ]}
                    />
                  </div>
                  
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-primary/10 rounded-full">
                          <Mail className="h-4 w-4" />
                        </div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                      </div>
                    </div>
                    <CardDescription className="flex justify-between pt-1">
                      <Badge variant="outline">{template.category}</Badge>
                      {template.is_default && (
                        <Badge className="ml-2">Default</Badge>
                      )}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent>
                    <p className="font-medium text-sm">{template.subject}</p>
                    <p className="line-clamp-3 text-sm text-muted-foreground mt-2">
                      {template.content}
                    </p>
                  </CardContent>
                  
                  <CardFooter className="flex justify-between pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditClick(template)}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicateClick(template.id)}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="list">
          {filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">
                  No templates found. Create your first template by clicking the "New Template" button.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="border rounded-md divide-y">
              {filteredTemplates.map((template) => (
                <div key={template.id} className="p-4 hover:bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-1.5 bg-primary/10 rounded-full">
                        <Mail className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center">
                          <h3 className="font-medium">{template.name}</h3>
                          {template.is_default && (
                            <Badge className="ml-2">Default</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {template.subject}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline">{template.category}</Badge>
                      <ActionMenu
                        actions={[
                          {
                            label: "Edit",
                            onClick: () => handleEditClick(template),
                            icon: <Pencil className="h-4 w-4" />,
                          },
                          {
                            label: "Duplicate",
                            onClick: () => handleDuplicateClick(template.id),
                            icon: <Copy className="h-4 w-4" />,
                          },
                          {
                            label: template.is_default
                              ? "Remove Default"
                              : "Set as Default",
                            onClick: () => handleSetAsDefault(
                              template.id,
                              template.category
                            ),
                            icon: template.is_default ? (
                              <StarOff className="h-4 w-4" />
                            ) : (
                              <Star className="h-4 w-4" />
                            ),
                          },
                          {
                            label: "Delete",
                            onClick: () => handleDeleteClick(template.id),
                            variant: "destructive",
                            icon: <Trash className="h-4 w-4" />,
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="category">
          {Object.keys(groupedTemplates).length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center">
                <p className="text-muted-foreground">
                  No templates found. Create your first template by clicking the "New Template" button.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedTemplates).map(([category, templates]) => (
                <div key={category}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <FolderOpen className="h-5 w-5 mr-2" />
                      {category}
                    </h2>
                    <Badge variant="outline">
                      {templates.length} template{templates.length !== 1 && "s"}
                    </Badge>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {templates.map((template) => (
                      <Card key={template.id} className="relative">
                        <div className="absolute top-2 right-2">
                          <ActionMenu
                            actions={[
                              {
                                label: "Edit",
                                onClick: () => handleEditClick(template),
                                icon: <Pencil className="h-4 w-4" />,
                              },
                              {
                                label: "Duplicate",
                                onClick: () => handleDuplicateClick(template.id),
                                icon: <Copy className="h-4 w-4" />,
                              },
                              {
                                label: template.is_default
                                  ? "Remove Default"
                                  : "Set as Default",
                                onClick: () => handleSetAsDefault(
                                  template.id,
                                  template.category
                                ),
                                icon: template.is_default ? (
                                  <StarOff className="h-4 w-4" />
                                ) : (
                                  <Star className="h-4 w-4" />
                                ),
                              },
                              {
                                label: "Delete",
                                onClick: () => handleDeleteClick(template.id),
                                variant: "destructive",
                                icon: <Trash className="h-4 w-4" />,
                              },
                            ]}
                          />
                        </div>
                        
                        <CardHeader className="pb-2">
                          <div className="flex items-center space-x-2">
                            <div className="p-1.5 bg-primary/10 rounded-full">
                              <Mail className="h-4 w-4" />
                            </div>
                            <CardTitle className="text-base">{template.name}</CardTitle>
                            {template.is_default && (
                              <Badge>Default</Badge>
                            )}
                          </div>
                        </CardHeader>
                        
                        <CardContent>
                          <p className="font-medium text-sm">{template.subject}</p>
                          <p className="line-clamp-3 text-sm text-muted-foreground mt-2">
                            {template.content}
                          </p>
                        </CardContent>
                        
                        <CardFooter className="flex justify-between pt-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(template)}
                          >
                            Edit
                          </Button>
                        </CardFooter>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Template Editor Dialog */}
      <EmailTemplateEditor
        isOpen={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSelectedTemplate(null);
        }}
        onSave={handleSaveTemplate}
        initialTemplate={selectedTemplate || undefined}
        isLoading={createMutation.isPending || updateMutation.isPending}
        defaultCategories={getDefaultTemplateCategories()}
        customCategories={categories}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Email Template"
        description="Are you sure you want to delete this email template? This action cannot be undone."
      />
    </div>
  );
}